import { createAction, createSlice, nanoid } from '@reduxjs/toolkit';
import { min, reduce, mapValues } from 'lodash';
import { Reducers } from '@sda/enums/store';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  MockStripeOrder, SKU, StripeOrderActionPayload, StripeState,
  CheckoutSessionPayload, ReserveTicketsPayload,
} from '@sda/interfaces/reducer/stripe.reducer';

export const startCreateStripeUnifiedSale = createAction<StripeOrderActionPayload>(`${Reducers.STRIPE}/startCreateStripeUnifiedSale`);
export const startReserveTickets = createAction<ReserveTicketsPayload>(`${Reducers.STRIPE}/startReserveTickets`);
export const startCheckoutSession = createAction<CheckoutSessionPayload>(`${Reducers.STRIPE}/startCheckoutSession`);
export const startCheckoutFreeTickets = createAction<{ shareContact: boolean, fullName?: string, email?: string }>(`${Reducers.STRIPE}/startCheckoutFreeTickets`);

const initialState: StripeState = {
  currentSKU: null,
  currentSKUAmount: {},
  currentOrder: null,
  idempotencyKey: nanoid(),
  maximumPurchaseQuantity: parseInt(process.env.MAX_TICKETS_PER_PURCHASE, 10),
};

const stripeSlice = createSlice({
  initialState,
  name: 'stripe',
  reducers: {
    resetBillingData: (state, action: PayloadAction<boolean>) => {
      Object.keys(state).forEach((key) => {
        state[key] = initialState[key];
      });

      state.currentSKU = action.payload ? initialState.currentSKU : state.currentSKU;
      state.currentSKUAmount = action.payload ? initialState.currentSKUAmount : state.currentSKUAmount;
      state.idempotencyKey = nanoid();
    },
    resetCurrentSKUAmount: (state) => {
      const numProducts = Object.keys(state.currentSKUAmount).length;

      state.currentSKUAmount = mapValues(state.currentSKUAmount, () => (numProducts === 1 ? 1 : 0));
    },
    setCurrentOrder: (state, action: PayloadAction<MockStripeOrder>) => {
      state.currentOrder = action.payload;
    },
    setCurrentSKU: (state, action: PayloadAction<SKU>) => {
      if (action.payload.enabled && typeof action.payload.price !== 'number') {
        const numberOfProducts = Object.keys(action.payload.price).length; // number of ticket types

        const currentSKUAmount = reduce(action.payload.price, (results, product, productId) => {
          if (results[productId]) {
            return results;
          }

          return {
            ...results,
            [productId]: numberOfProducts === 1 ? 1 : 0, // only pre-select a ticket for shows that do not have multiple ticket types
          };
        }, typeof state.currentSKUAmount !== 'number' ? state.currentSKUAmount : {});

        state.currentSKUAmount = currentSKUAmount;
      } else {
        state.currentSKUAmount = {};
      }

      state.currentSKU = action.payload;
    },
    setCurrentSKUAmount: (state, action: PayloadAction<{ productId: string, amount: number }>) => {
      state.currentSKUAmount[action.payload.productId] = min([action.payload.amount, state.maximumPurchaseQuantity]);
    },
    setCurrentSKUPrice: (state, action: PayloadAction<{ productId: string, price: number }>) => {
      state.currentSKU.price[action.payload.productId] = action.payload.price;
    },
    setMaximumPurchaseQuantity: (state, action: PayloadAction<number>) => {
      state.maximumPurchaseQuantity = action.payload;
    },
    updateStripeIdempotencyKey: (state) => {
      state.idempotencyKey = nanoid();
    },
  },
});

export const {
  resetBillingData, resetCurrentSKUAmount, setMaximumPurchaseQuantity, updateStripeIdempotencyKey,
  setCurrentOrder, setCurrentSKU, setCurrentSKUAmount, setCurrentSKUPrice,
} = stripeSlice.actions;

export default stripeSlice.reducer;
